import { useMemo, useState } from 'react'
import { useNotification } from 'hooks'
import { Table } from 'components/core-ui/Table'
import { getColumns, getFilters, toolbarActions } from './helpers'
import { makeCoreGetFileRequest, makeCoreGetRequest } from 'utils/api'
import { toTableGETUrl } from 'components/core-ui/Table/serializers'
import { toTableRowItems } from './serializers'
import { formatErrorMessage } from 'utils/formatting'
import { ViewActionIcon } from 'icons'
import { formatAndDownloadResponse, openPdfInNewTab } from "utils/helpers";
import s from '../../../commonStyles.modules.scss'
import {
  getDepositOptions,
  getParentSocietyOptions,
  getShopOptions,
  getHubOptions,
  getReturnTruckTypesOptions,
  getReturnTruckStatusOptions,
  getScreenPermissions
} from '../../api'
import { useNavigate } from 'react-router-dom'
import { SCREENS } from '../../constants'

const ReturnTruckList = () => {
  const [tableItems, setTableItems] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [totalCount, setTotalCount] = useState<number>(0)
  const [shouldRefreshData, setShouldRefreshData] = useState<boolean>(false)
  const [isExcelExportLoading, setIsExcelExportLoading] = useState<boolean>(false)

  const { showNotification } = useNotification()

  const navigate = useNavigate()

  const { data: shopOptions } = getShopOptions(showNotification)
  const { data: hubOptions } = getHubOptions(showNotification)
  const { data: despositOptions } = getDepositOptions(showNotification)
  const { data: returnTruckTypesOptions } = getReturnTruckTypesOptions(showNotification)
  const { data: returnTruckStatusOptions } = getReturnTruckStatusOptions(showNotification)
  const { data: parentSocietyOptions } = getParentSocietyOptions(showNotification)

  const { data: permissions } = getScreenPermissions(
    showNotification,
    SCREENS.PACKAGING_RETURN_TRUCK
  )

  const filters = useMemo(
    () =>
      getFilters({
        shopOptions,
        hubOptions,
        despositOptions,
        returnTruckTypesOptions,
        returnTruckStatusOptions,
        parentSocietyOptions
      }),
    [
      shopOptions,
      hubOptions,
      despositOptions,
      returnTruckTypesOptions,
      returnTruckStatusOptions,
      parentSocietyOptions
    ]
  )

  const download = async id => {
    try {
      const response = await makeCoreGetFileRequest(`/shop-reception/return-trucks/${id}/print`, {
        responseType: 'blob'
      })

      openPdfInNewTab(response)
    } catch (error) {
      showNotification({
        message: `A aparut o eroare la printare. ${formatErrorMessage(error)}`,
        type: 'error'
      })
    }
  }

  const columns = useMemo(() => getColumns(download, permissions?.canView), [permissions?.canView])

  const fetchData = async ({ page, rowsPerPage, order, orderBy, tableFilters }) => {
    setIsLoading(true)
    try {
      const response = await makeCoreGetRequest(
        toTableGETUrl({
          url: '/shop-reception/return-trucks',
          tableFilters,
          page,
          rowsPerPage,
          order,
          orderBy
        })
      )
      setTableItems(toTableRowItems(response.list))
      setTotalCount(response.page?.totalCount || 0)
      setIsLoading(false)
    } catch (error) {
      showNotification({
        message: `A aparut o eroare la obtinerea datelor din tabel. ${formatErrorMessage(error)}`,
        type: 'error'
      })
    }
  }

  const exportFileExcel = async (tableState, e) => {
    setIsExcelExportLoading(true)
    const { filters, order, orderBy } = tableState
    const url = toTableGETUrl({
      url: '/shop-reception/return-trucks/export',
      tableFilters: filters,
      order,
      orderBy
    })

    try {
      const response = await makeCoreGetFileRequest(url, {
        responseType: 'blob'
      })
      formatAndDownloadResponse(response);
      setIsExcelExportLoading(false)
    } catch (error) {
      setIsExcelExportLoading(false)
      showNotification({
        message: `A aparut o eroare la descarcarea fisierului. ${formatErrorMessage(error)}`,
        type: 'error'
      })
    }
  }

  const toolbar = useMemo(() => toolbarActions({
      totalCount,
      exportFileExcel: exportFileExcel,
      isExcelExportLoading }),
    [totalCount, isExcelExportLoading])

  return (
    <div className={s.mainContainer}>
      <Table
        hideCheckbox
        columns={columns}
        items={tableItems}
        fetchData={fetchData}
        isLoading={isLoading}
        totalCount={totalCount}
        shouldRefreshData={shouldRefreshData}
        setShouldRefreshData={setShouldRefreshData}
        toolbarActions={
          permissions?.canDownload
            ? toolbar
            : []
        }
        rowActions={[
          {
            label: 'Detalii',
            labelColor: 'white',
            typographyVariant: 'medium',
            backgroundColor: '#16a085',
            desktopIcon: <ViewActionIcon />,
            onClick: (e, rowId) => {
              navigate(`/shop-reception/package/trucksReturn/${rowId}`)
            }
          }
        ]}
        rowActionsWidth="130px"
        filters={filters}
        filtersButtonLabel="Filtre"
        filtersLabel="Filtre"
        appliedFiltersLabel="Filtre aplicate"
        cancelFiltersButtonLabel="Anuleaza"
        applyFiltersButtonLabel="Aplica"
        rowsPerPage={10}
        rowsPerPageOptions={[10, 25, 50]}
      />
    </div>
  )
}

export default ReturnTruckList
